import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { LANGUAGES, t } from "../utils/i18n";
import UserMenu from "../components/UserMenu";
import "./Navigation.css";

const Navigation = ({ onLoginClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const { lang, setLang, dir } = useLanguage();
  const { user, authLoading } = useAuth();
  const navText = {
    home: t({ en: "Home", de: "Startseite", fa: "خانه" }, lang),
    about: t({ en: "About Us", de: "Uber Uns", fa: "درباره ما" }, lang),
    mission: t({ en: "Our Mission", de: "Unsere Mission", fa: "ماموریت ما" }, lang),
    values: t({ en: "Our Values", de: "Unsere Werte", fa: "ارزش های ما" }, lang),
    work: t({ en: "What We Do", de: "Was Wir Tun", fa: "کارهای ما" }, lang),
    team: t({ en: "Our Team", de: "Unser Team", fa: "تیم ما" }, lang),
    projects: t({ en: "Projects", de: "Projekte", fa: "پروژه ها" }, lang),
    policies: t({ en: "Policies", de: "Richtlinien", fa: "سیاست ها" }, lang),
    membership: t({ en: "Membership", de: "Mitgliedschaft", fa: "عضویت" }, lang),
    news: t({ en: "News & Media", de: "Nachrichten & Medien", fa: "اخبار و رسانه" }, lang),
    library: t({ en: "Library", de: "Bibliothek", fa: "کتابخانه" }, lang),
    donate: t({ en: "Donate", de: "Spenden", fa: "حمایت" }, lang),
    login: t({ en: "Log In", de: "Anmelden", fa: "ورود" }, lang),
    toggleMenu: t({ en: "Toggle menu", de: "Menu umschalten", fa: "تغییر منو" }, lang),
    loginAria: t(
      {
        en: "Log in to your account",
        de: "In Ihr Konto einloggen",
        fa: "ورود به حساب کاربری",
      },
      lang
    ),
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on navigation
  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`navigation ${scrolled ? "scrolled" : ""}`} dir={dir}>
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          <img src="/pfm_logo.avif" alt="PFM Movement" />
          <span className="logo-text">PFM</span>
        </Link>

        <div className="mobile-nav-tools">
          <div className="lang-switcher mobile-lang-switcher">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                className={`lang-btn ${lang === l.code ? "active" : ""}`}
                onClick={() => setLang(l.code)}
                title={l.name}
              >
                {l.label}
              </button>
            ))}
          </div>

          {!authLoading && (
            <>
              {user ? (
                <UserMenu />
              ) : (
                <button
                  className="mobile-icon-btn mobile-login-btn"
                  onClick={onLoginClick}
                  aria-label={navText.loginAria}
                  title={navText.login}
                >
                  <svg width="18" height="18" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                    <circle cx="7.5" cy="5" r="2.75" stroke="currentColor" strokeWidth="1.3" />
                    <path d="M2 13c0-3 2.5-4.5 5.5-4.5S13 10 13 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </>
          )}

          <Link
            to="/donate"
            className="mobile-icon-btn mobile-donate-btn"
            aria-label={navText.donate}
            title={navText.donate}
            onClick={handleNavClick}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 21C12 21 3 14.5 3 8.5C3 5.42 5.42 3 8.5 3C10.24 3 11.91 3.81 13 5.08C14.09 3.81 15.76 3 17.5 3C20.58 3 23 5.42 23 8.5C23 14.5 14 21 12 21Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>

          <button
            className={`mobile-menu-toggle ${mobileMenuOpen ? "open" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={navText.toggleMenu}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        <ul className={`nav-links ${mobileMenuOpen ? "mobile-open" : ""}`}>
          <li key="home">
            <Link to="/" onClick={handleNavClick}>
              {navText.home}
            </Link>
          </li>
          <li 
            key="about"
            className="nav-dropdown"
            onMouseEnter={() => setAboutDropdownOpen(true)}
            onMouseLeave={() => setAboutDropdownOpen(false)}
          >
            <Link to="/about-us" onClick={handleNavClick}>
              {navText.about}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <ul className={`dropdown-menu ${aboutDropdownOpen ? 'open' : ''}`}>
              <li><Link to="/about-us#mission" onClick={handleNavClick}>{navText.mission}</Link></li>
              <li><Link to="/about-us#values" onClick={handleNavClick}>{navText.values}</Link></li>
              <li><Link to="/about-us#work" onClick={handleNavClick}>{navText.work}</Link></li>
              <li><Link to="/about-us#team" onClick={handleNavClick}>{navText.team}</Link></li>
              <li className="dropdown-divider"></li>
              <li><Link to="/projects" onClick={handleNavClick}>{navText.projects}</Link></li>
              <li><Link to="/policy" onClick={handleNavClick}>{navText.policies}</Link></li>
            </ul>
          </li>
          <li key="membership">
            <Link to="/membership" onClick={handleNavClick}>
              {navText.membership}
            </Link>
          </li>
          <li key="news">
            <Link to="/news" onClick={handleNavClick}>
              {navText.news}
            </Link>
          </li>
          <li key="library">
            <Link to="/library" onClick={handleNavClick}>
              {navText.library}
            </Link>
          </li>
        </ul>

        <div className="nav-cta">
          <div className="lang-switcher">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                className={`lang-btn ${lang === l.code ? "active" : ""}`}
                onClick={() => setLang(l.code)}
                title={l.name}
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* Auth area */}
          {!authLoading && (
            <>
              {user ? (
                <UserMenu />
              ) : (
                <button
                  className="btn btn-login"
                  onClick={onLoginClick}
                  aria-label={navText.loginAria}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="7.5"
                      cy="5"
                      r="2.75"
                      stroke="currentColor"
                      strokeWidth="1.3"
                    />
                    <path
                      d="M2 13c0-3 2.5-4.5 5.5-4.5S13 10 13 13"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                  </svg>
                  {navText.login}
                </button>
              )}
            </>
          )}

          <Link to="/donate" className="btn btn-primary">
            {navText.donate}
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
